// The iso world, shared by server and client (2026-10-14 rebuild; design: /opt/incentre/gaming/HOLLOWMARK-DESIGN.md,
// adapted in docs/DESIGN.md). One model for the street and for every dungeon floor:
//
//   a grid of 0.5 m CELLS (CELL_PX = 4 logical px; the world keeps 8 px per metre) with layers
//     g ground, s structure component, m wall material, h wall height (0.25 m units), b building id
//   + PROPS (furniture, lamps, cars, Hollow pods), each with its own hp.
//
// Everything a player can see is built from components, and most components can be destroyed: a wall's hp
// is its struct hp x its material's hp_mult (concrete and dungeon stone are 0 = indestructible; Matt,
// 2026-10-14: "some walls won't be, ie: concrete"). The server owns damage; clients apply its deltas.

import TYPES from './isotypes.json';
import { Grid, C_WALK, C_BUILDING, C_PROP, C_CAR, C_FURN } from './world';
import { rng } from './dungeon';

export const CELL_PX = 4;                 // logical px per cell (0.5 m at 8 px/m)
export const T = TYPES as any;

/** An upper floor of a building (pipeline/build_iso.py author_unit): its own small map in LOCAL cells, whose origin
 *  is world cell (ox, oy); drawn z metres up. Stairs: walking onto `up` (world cells, inclusive rect) on the street
 *  puts you at `arrive_up` (local cells); walking onto `down` (local) upstairs puts you at `arrive_down` (world). */
export interface FloorDef {
  id: number; venue: string; bld: number; level: number; z: number; ox: number; oy: number; w: number; h: number; name: string;
  up: [number, number, number, number]; arrive_up: [number, number]; down: [number, number, number, number]; arrive_down: [number, number];
  props: Prop[]; lights: { x: number; y: number; z: number; r: number; c: string }[]; npc_spots: Record<string, [number, number]>; spawn: [number, number];
  layers: string;   // base64 of g, s, m, ht (w*h bytes each) then b (u16 LE), the world.bin layout
}
export function decodeFloor(f: FloorDef, raw: Uint8Array): IsoMap { return IsoMap.decode(f.w, f.h, raw, f.props); }
/** An ECHO (studio 008, docs/levels/ch1-records-room.md): an authored Hollow floor, a reconstruction from memory, in its
 *  own LOCAL cells (client/public/iso/echoes/<id>.json, built by tools/echo/build_echo.py). The server makes one
 *  instance per party (map ids >= DUNGEON_BASE, beside the Cellars); the client builds it under map key 'echo', its
 *  index in ECHO_IDS carried in the Map message's seed. Walking into the street's `door` cells enters it at `arrive`;
 *  walking onto any `exits` rect puts you back on the street at `street_arrive`. `spots` are the set pieces the echo's
 *  own logic reads (for the records room: the hearing, 008-hearing.md section 1). */
export interface EchoDef {
  id: string; name: string; w: number; h: number; layers: string; props: Prop[];
  lights: { x: number; y: number; z: number; r: number; c: string }[];
  arrive: [number, number]; face: string; exits: [number, number, number, number][]; street_arrive: [number, number]; door: [number, number, number];
  spots: Record<string, any>; labels: { x: number; y: number; text: string }[];
}
export const ECHO_IDS = ['records_room'];
export function decodeEcho(e: EchoDef, raw: Uint8Array): IsoMap { return IsoMap.decode(e.w, e.h, raw, e.props); }
/** climbing through a smashed pane or shop window (review R2-010): over broken glass you go at 40% unless you are
 *  sneaking (crouched, already slow, picking your way). Server and client both apply it, so prediction agrees. */
export function glassMult(map: IsoMap, x: number, y: number, mods: number): number {
  const cx = Math.floor(x / CELL_PX), cy = Math.floor(y / CELL_PX);
  return map.inside(cx, cy) && map.s[cx + cy * map.w] === S.smashed && !(mods & 2) ? 0.4 : 1;   // 2 = Move.Crouch
}
export function inRect(r: [number, number, number, number], cx: number, cy: number) { return cx >= r[0] && cx <= r[2] && cy >= r[1] && cy <= r[3]; }
/** cycle 011 playability #6 (pt41d: E offered "TALK: PLAYMAKER" from 75 px, across the room): you reach a person to talk
 *  within the reach a door's label comes up at (6 cells and a half, 26 px), or across a counter or table between you
 *  (furniture on the line) within 44 px: Rocco behind the bar from the stools. Server (nearestNpc) and client (TALK) agree. */
export const TALK_REACH = 26, TALK_REACH_COUNTER = 44;
export function talkInReach(g: Grid, ax: number, ay: number, bx: number, by: number): boolean {
  const d = Math.hypot(bx - ax, by - ay);
  if (d <= TALK_REACH) return true;
  if (d > TALK_REACH_COUNTER) return false;
  const n = Math.ceil(d / 2);
  for (let i = 1; i < n; i++) { const x = ax + (bx - ax) * i / n, y = ay + (by - ay) * i / n, tx = Math.floor(x / g.tile), ty = Math.floor(y / g.tile); if (tx >= 0 && ty >= 0 && tx < g.w && ty < g.h && (g.cells[ty * g.w + tx] === C_PROP || g.cells[ty * g.w + tx] === C_FURN)) return true; }
  return false;
}

export interface Prop { id: number; t: string; x: number; y: number; w: number; d: number; face?: string; col?: string; venue?: string; wreck?: number; hp?: number; dead?: boolean; deco?: string; district?: string; board?: string; v?: number }   // district: exists only once that key of the district's shared state is set (studio 008); board: a hub board

const keyIndex = (sec: string) => { const o: Record<string, number> = {}; for (const [i, v] of Object.entries(T[sec])) o[(v as any).key] = Number(i); return o; };
export const G = keyIndex('ground');
export const S = keyIndex('struct');
export const M = keyIndex('material');

export function structDef(s: number): any { return T.struct[String(s)] ?? T.struct['0']; }
export function matDef(m: number): any { return T.material[String(m)] ?? T.material['0']; }
export function propDef(t: string): any { return T.prop[t]; }

export function structBlocks(s: number) { return !!structDef(s).blocks; }
export function isGlassy(s: number) { const d = structDef(s); return !!(d.base_glass || d.glass_z) && d.key !== 'wall'; }

/** Starting hp of a structure cell; 0 = cannot be destroyed. */
export function cellMaxHp(s: number, m: number): number {
  const d = structDef(s);
  if (!d.hp) return 0;
  if (d.key === 'wall' || d.key === 'fence' || d.key === 'railing') {
    const k = matDef(m).hp_mult ?? 1;
    return k ? Math.round(d.hp * k) : 0;
  }
  return d.hp;
}
/** What a structure becomes when it breaks. */
export function brokenStruct(s: number): number {
  const k = structDef(s).key;
  if (k === 'storefront' || k === 'window' || k === 'partition') return S.smashed;
  if (k === 'door' || k === 'door_glass' || k === 'door_open') return S.rubble;
  return S.rubble;
}

export class IsoMap {
  w: number; h: number;
  g: Uint8Array; s: Uint8Array; m: Uint8Array; ht: Uint8Array; b: Uint16Array;
  props: Prop[] = [];
  propById = new Map<number, Prop>();
  propAt: Int32Array;                 // cell -> prop id (any prop footprint, 0 = none)
  bar = new Map<number, number>();    // cell -> barricade hp (boards nailed over a window or door)
  carAt = new Map<number, number>();  // cell -> vehicle id stamped there this tick
  grid: Grid;
  version = 0;                        // bumps on every change, so renderers know to rebuild
  dirty = new Set<number>();          // changed cells since the renderer last looked

  constructor(w: number, h: number, layers?: { g: Uint8Array; s: Uint8Array; m: Uint8Array; ht: Uint8Array; b: Uint16Array }) {
    this.w = w; this.h = h;
    const n = w * h;
    this.g = layers?.g ?? new Uint8Array(n); this.s = layers?.s ?? new Uint8Array(n); this.m = layers?.m ?? new Uint8Array(n);
    this.ht = layers?.ht ?? new Uint8Array(n); this.b = layers?.b ?? new Uint16Array(n);
    this.propAt = new Int32Array(n);
    this.grid = new Grid(w, h, new Uint8Array(n), CELL_PX);
    // door magnetism (cycle 009b, world.ts stepOnce): the grid asks the map whether a point is in an OPEN doorway
    this.grid.doorAt = (px: number, py: number) => { const cx = Math.floor(px / CELL_PX), cy = Math.floor(py / CELL_PX); if (cx < 0 || cy < 0 || cx >= this.w || cy >= this.h) return false;
      const d = structDef(this.s[cy * this.w + cx]); return !!d.door && !!d.open; };
  }

  static decode(w: number, h: number, raw: Uint8Array, props: Prop[]): IsoMap {
    const n = w * h;
    // a real copy: raw may be a Node Buffer, whose slice() is a VIEW (R6-005: the server's 'pristine' street copy
    // shared memory with the live map, so regrowth restored broken cells to broken)
    const cp = (o: number) => new Uint8Array(raw.subarray(o, o + n));
    const bb = new Uint16Array(n);
    const dv = new DataView(raw.buffer, raw.byteOffset + 4 * n, 2 * n);
    for (let i = 0; i < n; i++) bb[i] = dv.getUint16(i * 2, true);
    const map = new IsoMap(w, h, { g: cp(0), s: cp(n), m: cp(2 * n), ht: cp(3 * n), b: bb });
    map.setProps(props);
    return map;
  }

  setProps(props: Prop[]) {
    this.props = props.map(p => ({ ...p }));
    this.propById.clear();
    this.propAt.fill(0);
    for (const p of this.props) {
      this.propById.set(p.id, p);
      if (p.dead) continue;
      for (let j = 0; j < p.d; j++) for (let i = 0; i < p.w; i++) {
        const x = p.x + i, y = p.y + j;
        if (x >= 0 && y >= 0 && x < this.w && y < this.h) this.propAt[y * this.w + x] = p.id;
      }
    }
    this.rebuildGrid();
  }

  idx(x: number, y: number) { return y * this.w + x; }
  inside(x: number, y: number) { return x >= 0 && y >= 0 && x < this.w && y < this.h; }

  rebuildGrid() { for (let i = 0; i < this.w * this.h; i++) this.updateCell(i); }
  updateCell(i: number) {
    const s = this.s[i];
    let c = C_WALK;
    if (this.bar.has(i)) c = C_BUILDING;
    else if (s && structBlocks(s)) c = C_BUILDING;
    else if (this.g[i] === G.void) c = C_BUILDING;
    else {
      const pid = this.propAt[i];
      if (pid) { const p = this.propById.get(pid)!; if (!p.dead && propDef(p.t)?.blocks) c = this.b[i] ? C_FURN : C_PROP; }   // (indoors: furniture, the narrower body; world.ts)
    }
    if (c === C_WALK && this.carAt.has(i)) c = C_CAR;
    this.grid.cells[i] = c;
  }
  setBarricade(i: number, hp: number) {
    if (hp > 0) this.bar.set(i, hp); else this.bar.delete(i);
    this.updateCell(i); this.version++; this.dirty.add(i);
  }
  /** stamp a vehicle's footprint so bodies (and the client's prediction) collide with it */
  stampCar(id: number, cells: number[]) {
    for (const i of cells) { this.carAt.set(i, id); this.updateCell(i); }
  }
  unstampCar(id: number, cells: number[]) {
    for (const i of cells) if (this.carAt.get(i) === id) { this.carAt.delete(i); this.updateCell(i); }
  }

  setStruct(i: number, s: number) {
    if (this.s[i] === s) return;
    this.s[i] = s;
    if (s === S.rubble) this.ht[i] = Math.min(this.ht[i], 2);
    this.updateCell(i); this.version++; this.dirty.add(i);
  }
  killProp(id: number) {
    const p = this.propById.get(id);
    if (!p || p.dead) return;
    p.dead = true;
    for (let j = 0; j < p.d; j++) for (let i = 0; i < p.w; i++) {
      const x = p.x + i, y = p.y + j;
      if (!this.inside(x, y)) continue;
      const k = this.idx(x, y);
      if (this.propAt[k] === id) { this.propAt[k] = 0; this.updateCell(k); this.dirty.add(k); }
    }
    this.version++;
  }
  /** the street mends (R6-005): a broken prop comes back, re-stamped into its cells */
  reviveProp(id: number) {
    const p = this.propById.get(id);
    if (!p || !p.dead) return;
    p.dead = false;
    for (let j = 0; j < p.d; j++) for (let i = 0; i < p.w; i++) {
      const x = p.x + i, y = p.y + j;
      if (!this.inside(x, y)) continue;
      const k = this.idx(x, y);
      if (!this.propAt[k]) { this.propAt[k] = id; this.updateCell(k); this.dirty.add(k); }
    }
    this.version++;
  }
  /** building id under a logical-px point */
  bldAt(px: number, py: number) {
    const x = Math.floor(px / CELL_PX), y = Math.floor(py / CELL_PX);
    return this.inside(x, y) ? this.b[this.idx(x, y)] : 0;
  }
}

// ------------------------------------------------------------------------------------------------
// THE CELLARS: the first Hollow dungeon, under the Tavern (docs/STORY.md: the basement nobody knew
// was there). Floor 1 starts in the real basement footprint - 10 x 14 m of concrete under the bar,
// kegs and junk - and breaks through the foundation into the Hollow: organic chambers of earth that
// turn to wet teal Hollow-stuff the deeper you go, egg-sacs that can be smashed (and sometimes hatch),
// bones of the turned, and the thing at the bottom.
//
// Deterministic from the seed: the client builds the same geometry; the server's spawns and loot come
// from its own RNG stream.

export const HOLLOW_GEN = 5;   // 4: a shrine in some floors (more of them deeper); 5: the hatch down in the far chamber, floors 2+ are dug, 4+ all Hollow

export interface HollowFloor {
  map: IsoMap; seed: number; depth: number;
  entry: { x: number; y: number };          // logical px: the foot of the ladder up
  rooms: { x: number; y: number; r: number }[];   // cell centres + radius
  bossRoom: number;
  boss: { x: number; y: number };
}

export function generateHollow(seed: number, depth = 1): HollowFloor {
  const r = rng(seed ^ 0x51ed270b);
  const ri = (a: number, b: number) => a + Math.floor(r() * (b - a + 1));
  const W = 150, H = 150;
  const map = new IsoMap(W, H);
  const n = W * H;
  map.g.fill(G.void);
  const floor = new Uint8Array(n);          // 1 = basement, 2 = earth, 3 = hollow
  // the basement: the Tavern's footprint, 22 x 30 cells, top-left at (6, 6)
  const BX = 6, BY = 6, BW = 22, BH = 30;
  // floor 2 and deeper: the same footprint, but dug out of the earth under the floor above (no bar basement down here)
  const dug = depth >= 2, allHollow = depth >= 4;
  for (let y = BY + 1; y < BY + BH - 1; y++) for (let x = BX + 1; x < BX + BW - 1; x++) floor[y * W + x] = dug ? 2 : 1;
  // chambers, walked away from the basement's breach in the south-east wall
  const rooms: { x: number; y: number; r: number }[] = [{ x: BX + BW / 2, y: BY + BH / 2, r: 8 }];
  let cx = BX + BW + 6, cy = BY + BH - 4;
  const target = 7 + Math.min(4, depth);
  for (let tries = 0; rooms.length < target + 1 && tries < 300; tries++) {
    const ang = (r() * 1.4 + 0.1) * Math.PI / 2;          // drift south-east, so the floor reads as going deeper
    const dist = ri(18, 28);
    const nx = Math.round(cx + Math.cos(ang) * dist), ny = Math.round(cy + Math.sin(ang) * dist);
    const rad = ri(6, 11);
    if (nx - rad < 3 || ny - rad < 3 || nx + rad > W - 4 || ny + rad > H - 4) { cx = rooms[1 + ri(0, Math.max(0, rooms.length - 2))]?.x ?? cx; cy = rooms[1 + ri(0, Math.max(0, rooms.length - 2))]?.y ?? cy; continue; }
    if (rooms.some(o => Math.hypot(o.x - nx, o.y - ny) < o.r + rad + 3)) continue;
    rooms.push({ x: nx, y: ny, r: rad });
    if (r() < 0.7) { cx = nx; cy = ny; }
  }
  // carve blobs (jittered circles) and tunnels; the boss chamber is the farthest, and big
  let far = 1, fd = 0;
  for (let i = 1; i < rooms.length; i++) { const d = Math.hypot(rooms[i].x - BX, rooms[i].y - BY); if (d > fd) { fd = d; far = i; } }
  rooms[far].r = Math.max(rooms[far].r, 12);
  const carve = (x: number, y: number, v: number) => { if (x > 1 && y > 1 && x < W - 2 && y < H - 2 && !floor[y * W + x]) floor[y * W + x] = v; };
  rooms.forEach((o, i) => {
    if (i === 0) return;
    const k1 = r() * 6, k2 = r() * 6;
    for (let y = o.y - o.r - 3; y <= o.y + o.r + 3; y++) for (let x = o.x - o.r - 3; x <= o.x + o.r + 3; x++) {
      const a = Math.atan2(y - o.y, x - o.x);
      const rr = o.r * (1 + 0.18 * Math.sin(a * 3 + k1) + 0.1 * Math.sin(a * 5 + k2));
      if (Math.hypot(x - o.x, y - o.y) <= rr) carve(x, y, allHollow || i > rooms.length / 2 || i === far ? 3 : 2);
    }
  });
  const tunnel = (a: { x: number; y: number }, b: { x: number; y: number }, v: number) => {
    const steps = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y));
    const wob = r() * 6;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const px = a.x + (b.x - a.x) * t + Math.sin(t * 6 + wob) * 2, py = a.y + (b.y - a.y) * t + Math.cos(t * 5 + wob) * 2;
      const w = 2 + (s % 7 < 3 ? 1 : 0);
      for (let dy = -w; dy <= w; dy++) for (let dx = -w; dx <= w; dx++) if (dx * dx + dy * dy <= w * w + 1) carve(Math.round(px + dx), Math.round(py + dy), v);
    }
  };
  // the breach: out of the basement's south-east corner into chamber 1
  const breach = { x: BX + BW - 1, y: BY + BH - 6 };
  tunnel(breach, rooms[1], 2);
  // spanning tree by nearest connected, plus two loops
  const linked = [1];
  for (let i = 2; i < rooms.length; i++) {
    let best = linked[0], bd = 1e9;
    for (const j of linked) { const d = Math.hypot(rooms[i].x - rooms[j].x, rooms[i].y - rooms[j].y); if (d < bd) { bd = d; best = j; } }
    tunnel(rooms[best], rooms[i], i > rooms.length / 2 ? 3 : 2); linked.push(i);
  }
  for (let k = 0; k < 2 && rooms.length > 4; k++) { const a = ri(1, rooms.length - 1), b = ri(1, rooms.length - 1); if (a !== b) tunnel(rooms[a], rooms[b], 3); }
  // the breach cells in the basement wall become rubble you walk over
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x, f = floor[i];
    if (f) { map.g[i] = f === 1 ? G.basement : f === 2 ? G.earth : G.hollow; continue; }
  }
  // walls: any non-floor cell touching floor (8-neighbour)
  for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
    const i = y * W + x;
    if (floor[i]) continue;
    let near = 0;
    for (let dy = -1; dy <= 1 && !near; dy++) for (let dx = -1; dx <= 1; dx++) { const f = floor[(y + dy) * W + x + dx]; if (f) { near = f; break; } }
    if (!near) continue;
    map.g[i] = near === 1 ? G.basement : near === 2 ? G.earth : G.hollow;
    map.s[i] = S.wall;
    map.m[i] = near === 1 ? M.concrete : near === 2 ? M.stone_dk : M.hollow;
    // cavern walls: low (so the room reads, Diablo-style) and smooth, a slow swell instead of a picket fence
    const swell = Math.sin(x * 0.21 + y * 0.13) + Math.sin(y * 0.17 - x * 0.07);
    map.ht[i] = near === 1 ? 11 : Math.round(8 + 1.6 * (swell + 2));
  }
  // knock the breach through the basement wall
  for (let y = breach.y - 2; y <= breach.y + 2; y++) for (let x = breach.x - 1; x <= breach.x + 2; x++) {
    const i = y * W + x;
    if (map.s[i] === S.wall && map.m[i] === M.concrete) { map.s[i] = S.rubble; map.ht[i] = 2; map.g[i] = G.earth; }
  }
  // props
  let pid = 1;
  const props: Prop[] = [];
  const free = (x: number, y: number, w: number, d: number) => {
    for (let j = -1; j <= d; j++) for (let i = -1; i <= w; i++) {
      const xx = x + i, yy = y + j;
      if (!map.inside(xx, yy)) return false;
      const k = yy * W + xx;
      if (j >= 0 && j < d && i >= 0 && i < w && (!floor[k] || map.s[k])) return false;
      if (props.some(p => xx >= p.x && xx < p.x + p.w && yy >= p.y && yy < p.y + p.d)) return false;
    }
    return true;
  };
  const put = (t: string, x: number, y: number, w?: number, d?: number) => {
    const spec = propDef(t);
    const pw = w ?? Math.max(1, Math.round(spec.m[0] * 2)), pd = d ?? Math.max(1, Math.round(spec.m[1] * 2));
    if (!free(x, y, pw, pd)) return null;
    const p: Prop = { id: pid++, t, x, y, w: pw, d: pd };
    props.push(p); return p;
  };
  // the ladder up, under the trapdoor (the Tavern's hatch is at u 5.6-6.6, v 13.2-14.2 -> cells 11..12, 26..27)
  const ladder: Prop = { id: pid++, t: 'ladder', x: BX + 11, y: BY + 26, w: 2, d: 2 };
  props.push(ladder);
  // the hatch down (audit item 13): in the far chamber, off-centre so the boss does not stand on it; it opens once the floor is cleared
  for (const [dx, dy] of [[4, 3], [-4, 3], [3, -4], [-3, -4], [5, 0], [0, 5], [-5, 0], [0, -5]]) if (put('hatch', rooms[far].x + dx, rooms[far].y + dy, 2, 2)) break;
  // twenty years of basement
  if (!dug) for (const [t, x, y] of [['keg', 2, 3], ['keg', 3, 3], ['keg', 2, 4], ['crate', 6, 2], ['crate', 8, 2], ['shelf', 13, 1], ['shelf', 17, 1],
    ['crate', 18, 10], ['cooler', 2, 14], ['crate', 4, 20], ['keg', 16, 22], ['keg', 17, 22]] as [string, number, number][]) put(t, BX + x, BY + y);
  rooms.forEach((o, i) => {
    if (i === 0) return;
    const boss = i === far;
    const k = boss ? 4 : ri(2, 5);
    for (let j = 0; j < k * 3; j++) {
      const a = r() * Math.PI * 2, d = r() * (o.r - 2);
      const x = Math.round(o.x + Math.cos(a) * d), y = Math.round(o.y + Math.sin(a) * d);
      const t = boss ? (j % 2 ? 'pillar' : 'bones') : ['pod', 'pod', 'bones', 'crate', 'pillar'][ri(0, 4)];
      put(t, x, y);
    }
  });
  // a shrine in about a third of floors, more often deeper, in a chamber that is neither the first nor the boss's
  if (rooms.length > 3 && r() < Math.min(0.75, 0.3 + 0.1 * (depth - 1))) {
    const k = ri(2, rooms.length - 1), o = rooms[k === far ? 1 : k];
    for (let t = 0; t < 12; t++) { const a = r() * Math.PI * 2, d = r() * 3; if (put('shrine', Math.round(o.x + Math.cos(a) * d), Math.round(o.y + Math.sin(a) * d))) break; }
  }
  map.setProps(props);
  const cpx = (c: number) => (c + 0.5) * CELL_PX;
  return { map, seed, depth, entry: { x: cpx(ladder.x + 1), y: cpx(ladder.y - 1) }, rooms, bossRoom: far, boss: { x: cpx(rooms[far].x), y: cpx(rooms[far].y) } };
}

// ------------------------------------------------------------------------------------------------
// projection (client and tools). Logical px (8/m) -> screen px. 1 m of ground = a 32 x 16 diamond;
// 1 m of height = 26 px, so a 6 ft (1.83 m) person is ~48 px and a 2.1 m door ~55 px (Matt, 2026-10-14:
// "an average 6ft tall person is most of the size of the door"). Screen-up is world NORTH-WEST.
export const ISO_X = 2, ISO_Y = 1, Z_PX = 26;
export function toScreen(x: number, y: number, zM = 0): [number, number] { return [(x - y) * ISO_X, (x + y) * ISO_Y - zM * Z_PX]; }
export function fromScreen(sx: number, sy: number): [number, number] { const a = sx / ISO_X, c = sy / ISO_Y; return [(a + c) / 2, (c - a) / 2]; }
/** a screen-space stick/keys direction -> world direction (screen up = north-west) */
export function screenDirToWorld(dx: number, dy: number): [number, number] {
  if (!dx && !dy) return [0, 0];
  const wx = dx / 2 + dy, wy = dy - dx / 2;
  const l = Math.hypot(wx, wy) || 1, m = Math.min(1, Math.hypot(dx, dy));
  return [wx / l * m, wy / l * m];
}

// ------------------------------------------------------------------------------------------------
// vehicles (Matt, 2026-10-14: "Make the vehicles driveable like Project Zomboid")
export const CAR_MODELS = ['sedan', 'pickup', 'hatchback', 'van', 'wagon', 'police'];
export const CAR_COLOURS = ['#8e2a2a', '#2a4a7a', '#c8c8c0', '#1e1e22', '#4a6a3a', '#b8a060', '#7a2a6a', '#b06a34', '#5a5a62', '#3a7a7a'];
// cycle 011 round 2, the scale bible: the world draws a vertical metre at 26 px but a ground metre at ~18, so a car (all
// length) read a quarter short beside a figure. Cars draw and collide at CAR_SCALE of their real size (client/src/actors.ts
// FIG_SCALE has the figures' half of it); CAR_DIMS is the scaled footprint, CAR_REAL the real one the sprites are cut at.
export const CAR_SCALE = 1.1;
export const CAR_REAL: Record<string, [number, number]> = { sedan: [4.3, 1.9], pickup: [4.6, 1.95], hatchback: [3.9, 1.8], van: [4.8, 2.0], wagon: [4.5, 1.9], police: [4.4, 1.9] };
export const CAR_DIMS: Record<string, [number, number]> = Object.fromEntries(Object.entries(CAR_REAL).map(([k, [l, w]]) => [k, [l * CAR_SCALE, w * CAR_SCALE]]));
/** the cells under a car whose centre is (x, y) logical px, heading h radians, size l x w metres */
export function carCells(map: { w: number; h: number }, x: number, y: number, h: number, l: number, w: number): number[] {
  const out: number[] = [];
  const c = Math.cos(h), s = Math.sin(h), hl = l * 4, hw = w * 4;     // half-sizes in logical px (8 px/m)
  const r = Math.ceil((hl + hw) / CELL_PX) + 1;
  const cx0 = Math.floor(x / CELL_PX), cy0 = Math.floor(y / CELL_PX);
  for (let cy = cy0 - r; cy <= cy0 + r; cy++) for (let cx = cx0 - r; cx <= cx0 + r; cx++) {
    if (cx < 0 || cy < 0 || cx >= map.w || cy >= map.h) continue;
    const px = (cx + 0.5) * CELL_PX - x, py = (cy + 0.5) * CELL_PX - y;
    const u = px * c + py * s, v = -px * s + py * c;
    if (Math.abs(u) <= hl && Math.abs(v) <= hw) out.push(cy * map.w + cx);
  }
  return out;
}
/** the eight screen views the car art comes in, from a world heading (radians, 0 = east, y south) */
/** 16 directions from the nine-view pages (tools/art/isoart.py CAR_VIEWS16): views v0..v8 are world headings
 *  45 down to -135 degrees; any other heading h is the screen mirror of heading 90-h */
export function carView16(h: number): { view: string; flip: boolean } {
  const k = ((Math.round(h / (Math.PI / 8)) % 16) + 16) % 16;         // 22.5-degree steps, 0 = east
  let deg = k * 22.5; if (deg > 180) deg -= 360;                      // (-180, 180]
  let flip = false;
  if (deg > 45 || deg < -135) { deg = 90 - deg; if (deg > 180) deg -= 360; if (deg <= -180) deg += 360; flip = true; }
  const i = Math.round((45 - deg) / 22.5);
  return { view: 'v' + Math.max(0, Math.min(8, i)), flip };
}
export function carView(h: number): { view: string; flip: boolean } {
  // world direction index: 0 E, 1 SE, 2 S, 3 SW, 4 W, 5 NW, 6 N, 7 NE
  const k = ((Math.round(h / (Math.PI / 4)) % 8) + 8) % 8;
  return ([{ view: 'e', flip: false }, { view: 'se', flip: false }, { view: 'e', flip: true }, { view: 'ne', flip: true },
    { view: 'n', flip: true }, { view: 'nw', flip: false }, { view: 'n', flip: false }, { view: 'ne', flip: false }])[k];
}

// ---- walking into a closed door opens it (server main.ts pushDoor; cycle 009b: client prediction runs the same rule, so
// your figure walks through the Tavern's door with the server instead of stopping and being pulled through a tick later)
/** the closed, unboarded door a body at (x, y) pushing (dx, dy) opens: nearest first, out to the corner assist's 8 px
 *  either side of the leading edge; -1 for none */
export function doorToPush(map: IsoMap, x: number, y: number, dx: number, dy: number): number {
  const l = Math.hypot(dx, dy) || 1, ux = dx / l, uy = dy / l;
  for (const o of [0, -2, 2, -4, 4, -6, 6, -8, 8]) {
    const cx = Math.floor((x + ux * 4 - uy * o) / CELL_PX), cy = Math.floor((y + uy * 4 + ux * o) / CELL_PX);
    if (!map.inside(cx, cy)) continue;
    const i = map.idx(cx, cy), d = structDef(map.s[i]);
    if (d.door && !d.open && !map.bar.has(i)) return i;
  }
  return -1;
}
/** the door cells that swing with door cell i (its run, up to 6) */
export function doorRun(map: IsoMap, door: number): number[] {
  const run: number[] = [], seen = new Set<number>([door]), q = [door];
  while (q.length && run.length < 6) {
    const i = q.shift()!; run.push(i);
    for (const n of [i - 1, i + 1, i - map.w, i + map.w]) if (!seen.has(n) && n >= 0 && n < map.w * map.h && structDef(map.s[n]).door) { seen.add(n); q.push(n); }
  }
  return run;
}
/** did a step (x0, y0) -> body go under 60% of the way it was pushed (sliding along a wall still counts)? pushDoor's test */
export function pushedInto(x0: number, y0: number, x: number, y: number, dx: number, dy: number, sp: number, dt: number): boolean {
  const l = Math.hypot(dx, dy) || 1;
  return ((x - x0) * dx + (y - y0) * dy) / l < 0.6 * sp * dt * Math.min(1, l);
}
